import React, { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { format } from "date-fns";
import {
  Banknote,
  CreditCard,
  QrCode,
  Landmark,
  ShoppingBag,
  Clock,
  AlertCircle,
  Search,
  Filter,
  RefreshCw,
  TrendingUp,
  Package,
  User,
  CheckCircle2,
  FileText,
  DollarSign,
  Truck,
  Eye,
  Calendar as CalendarIcon,
  Wallet,
  ArrowDown,
  ArrowUp,
  ArrowLeftRight,
  Home,
  X,
  ChevronDown,
  PlusCircle,
  Check,
} from "lucide-react";
import { DateRangePicker } from "../components/DateRangePicker";
import { toast } from "sonner";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (n) =>
  `₹${Math.abs(Number(n) || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const toDateInput = (d) => {
  if (!d) return "";
  try {
    return format(d, "yyyy-MM-dd");
  } catch {
    return "";
  }
};

const TX_META = {
  received_cash: { label: "Received", via: "Cash", dir: 1, color: "emerald" },
  received_bank: {
    label: "Received",
    via: "Bank/UPI",
    dir: 1,
    color: "emerald",
  },
  gave_cash: { label: "Gave", via: "Cash", dir: -1, color: "rose" },
  gave_bank: { label: "Gave", via: "Bank/UPI", dir: -1, color: "rose" },
  transfer_cash_to_bank: {
    label: "Transfer",
    via: "Cash→Bank",
    dir: 0,
    color: "purple",
  },
  transfer_bank_to_cash: {
    label: "Transfer",
    via: "Bank→Cash",
    dir: 0,
    color: "purple",
  },
  daybook_borrow_cash: {
    label: "Borrowed",
    via: "Cash",
    dir: 1,
    color: "emerald",
  },
  daybook_borrow_upi: {
    label: "Borrowed",
    via: "Bank/UPI",
    dir: 1,
    color: "emerald",
  },
  daybook_give_cash: { label: "Given", via: "Cash", dir: -1, color: "rose" },
  daybook_give_upi: { label: "Given", via: "Bank/UPI", dir: -1, color: "rose" },
  home_cash: { label: "Taken Home", via: "Cash", dir: -1, color: "amber" },
  home_upi: { label: "Taken Home", via: "Bank/UPI", dir: -1, color: "amber" },
  received_home_cash: {
    label: "Returned from Home",
    via: "Cash",
    dir: 1,
    color: "purple",
  },
  received_home_upi: {
    label: "Returned from Home",
    via: "Bank/UPI",
    dir: 1,
    color: "purple",
  },
};

const getEffectivePaymentMode = (tx) => {
  const tt = tx.transaction_type || "";
  if (tt === "transfer_cash_to_bank" || tt === "transfer_bank_to_cash")
    return "transfer";
  if (tx.payment_mode === "cash") return "cash";
  if (tx.payment_mode === "upi" || tx.payment_mode === "bank") return "bank";
  if (tt.includes("cash")) return "cash";
  if (tt.includes("bank") || tt.includes("upi")) return "bank";
  return "unknown";
};

export default function DayBook() {
  const { token, user } = useAuth();
  const { t } = useTranslation();
  const isAdminOrManager = ["admin", "manager"].includes(user?.role);
  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token],
  );

  // Primary View Mode: 'sales' (Daily Sales & Orders) | 'wallet' (Cash & Bank Operations)
  const [viewMode, setViewMode] = useState("sales");

  // Shared Date Range state
  const [dateRange, setDateRange] = useState({
    from: new Date(),
    to: new Date(),
  });

  const dateFrom = useMemo(() => toDateInput(dateRange?.from), [dateRange]);
  const dateTo = useMemo(
    () => toDateInput(dateRange?.to || dateRange?.from),
    [dateRange],
  );

  // ══════════════════════════════════════════════════════════════════
  // 1. SALES VIEW STATE
  // ══════════════════════════════════════════════════════════════════
  const [salesLoading, setSalesLoading] = useState(false);
  const [salesData, setSalesData] = useState({
    summary: {
      total_sales: 0,
      sales_cash_in: 0,
      sales_upi_in: 0,
      sales_card_in: 0,
      sales_bank_transfer_in: 0,
      total_sales_received: 0,
      past_collections_cash: 0,
      past_collections_upi: 0,
      past_collections_card: 0,
      past_collections_bank: 0,
      total_past_collections: 0,
      cash_in: 0,
      upi_in: 0,
      card_in: 0,
      bank_transfer_in: 0,
      total_received: 0,
      balance_due: 0,
      pending_delivery_count: 0,
      pending_delivery_value: 0,
      total_orders_count: 0,
      cash_out: 0,
      cash_in_other: 0,
      cash_net: 0,
      bank_out: 0,
      bank_in_other: 0,
      bank_net: 0,
    },
    orders: [],
    collections: [],
    drawer_transactions: [],
  });
  const [activeTab, setActiveTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  const fetchSalesData = useCallback(
    async (from, to) => {
      setSalesLoading(true);
      try {
        const res = await axios.get(`${API_URL}/daybook/daily-sales`, {
          headers,
          params: { date_from: from, date_to: to },
        });
        setSalesData(res.data);
      } catch (err) {
        toast.error("Failed to load daily sales data");
      } finally {
        setSalesLoading(false);
      }
    },
    [headers],
  );

  // ══════════════════════════════════════════════════════════════════
  // 2. WALLET & CASH DRAWER STATE
  // ══════════════════════════════════════════════════════════════════
  const [wallet, setWallet] = useState(null);
  const [walletLoading, setWalletLoading] = useState(false);
  const [walletSaving, setWalletSaving] = useState(false);

  // Wallet Quick Entry Form
  const [wTab, setWTab] = useState("received"); // 'received' | 'gave' | 'transfer'
  const [wVia, setWVia] = useState("cash"); // 'cash' | 'bank'
  const [wDest, setWDest] = useState("party"); // 'party' | 'home'
  const [wDir, setWDir] = useState("cash_to_bank"); // 'cash_to_bank' | 'bank_to_cash'
  const [wAmount, setWAmount] = useState("");
  const [wParty, setWParty] = useState("");
  const [wNotes, setWNotes] = useState("");
  const [wDate, setWDate] = useState(new Date().toISOString().split("T")[0]);

  // Wallet Transaction Filter
  const [txFilter, setTxFilter] = useState("all"); // 'all' | 'cash' | 'bank'
  const [txTypeFilter, setTxTypeFilter] = useState("all"); // 'all' | 'in' | 'out' | 'transfer'
  const [txSearch, setTxSearch] = useState("");

  const fetchWallet = useCallback(
    async (from, to) => {
      setWalletLoading(true);
      try {
        const res = await axios.get(`${API_URL}/wallet`, {
          headers,
          params: {
            date_from: from,
            date_to: to,
            limit: 2000,
          },
        });
        setWallet(res.data);
      } catch {
        /* soft fail */
      } finally {
        setWalletLoading(false);
      }
    },
    [headers],
  );

  // Trigger loads when dates change
  useEffect(() => {
    if (dateFrom && dateTo) {
      if (viewMode === "sales") {
        fetchSalesData(dateFrom, dateTo);
      } else {
        fetchWallet(dateFrom, dateTo);
      }
    }
  }, [dateFrom, dateTo, viewMode, fetchSalesData, fetchWallet]);

  // Handle Wallet Save
  const wTxType = useMemo(() => {
    if (wTab === "transfer") {
      return wDir === "cash_to_bank"
        ? "transfer_cash_to_bank"
        : "transfer_bank_to_cash";
    }
    if (wDest === "home") {
      if (wTab === "gave") return wVia === "cash" ? "home_cash" : "home_upi";
      if (wTab === "received")
        return wVia === "cash" ? "received_home_cash" : "received_home_upi";
    }
    return `${wTab === "received" ? "received" : "gave"}_${wVia === "cash" ? "cash" : "bank"}`;
  }, [wTab, wVia, wDir, wDest]);

  const wAvailBal = useMemo(() => {
    if (wTab === "gave") {
      return wVia === "cash"
        ? (wallet?.cash_balance ?? 0)
        : (wallet?.bank_balance ?? 0);
    }
    if (wTab === "received" && wDest === "home") {
      return wallet?.home_total ?? 0;
    }
    if (wTab === "transfer") {
      return wDir === "cash_to_bank"
        ? (wallet?.cash_balance ?? 0)
        : (wallet?.bank_balance ?? 0);
    }
    return null;
  }, [wTab, wVia, wDir, wDest, wallet]);

  const wInsufficient =
    wAvailBal !== null && parseFloat(wAmount) > wAvailBal && !!wAmount;

  const handleWalletSave = async () => {
    const amt = parseFloat(wAmount);
    if (!amt || amt <= 0) return toast.error("Enter a valid amount");
    if (wTab !== "transfer" && wDest !== "home" && !wParty.trim()) {
      return toast.error(
        wTab === "received"
          ? "Who gave you this money?"
          : "Who did you give this to?",
      );
    }
    if (wInsufficient)
      return toast.error(`Only ${fmtMoney(wAvailBal)} available`);

    setWalletSaving(true);
    try {
      await axios.post(
        `${API_URL}/wallet/transaction`,
        {
          transaction_type: wTxType,
          amount: amt,
          party_name: wDest === "home" ? "Home" : wParty.trim(),
          notes: wNotes.trim(),
          date: wDate,
        },
        { headers },
      );
      toast.success("Transaction recorded");
      setWAmount("");
      setWParty("");
      setWNotes("");
      setWDest("party");
      fetchWallet(dateFrom, dateTo);
      fetchSalesData(dateFrom, dateTo);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to record transaction");
    } finally {
      setWalletSaving(false);
    }
  };

  const handleWalletDelete = async (id) => {
    if (!window.confirm("Delete this transaction entry?")) return;
    try {
      await axios.delete(`${API_URL}/wallet/transaction/${id}`, { headers });
      toast.success("Transaction deleted");
      fetchWallet(dateFrom, dateTo);
      fetchSalesData(dateFrom, dateTo);
    } catch (err) {
      toast.error(
        err.response?.data?.detail || "Cannot delete system auto entries",
      );
    }
  };

  // ══════════════════════════════════════════════════════════════════
  // FILTERED LISTS
  // ══════════════════════════════════════════════════════════════════
  const summary = salesData.summary || {};
  const orders = salesData.orders || [];
  const collections = salesData.collections || [];

  const isSingleDayToday =
    dateFrom === toDateInput(new Date()) && dateTo === toDateInput(new Date());

  const filteredOrders = useMemo(() => {
    return orders.filter((order) => {
      const matchesSearch =
        !searchQuery.trim() ||
        (order.invoice_number || "")
          .toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        (order.customer_name || "")
          .toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        (order.customer_phone || "").includes(searchQuery);

      if (!matchesSearch) return false;

      const bal = Number(order.balance_due) || 0;
      const status = (order.status || "").toLowerCase();
      const pm = (order.payment_mode || "").toLowerCase().trim();
      const ccash = Number(order.credit_cash) || 0;
      const cupi = Number(order.credit_upi) || 0;
      const ccard = Number(order.credit_card) || 0;

      if (activeTab === "balance_due") return bal > 0.01;
      if (activeTab === "pending_delivery")
        return status === "pending_delivery";
      if (activeTab === "cash") return ccash > 0 || pm === "cash";
      if (activeTab === "upi")
        return cupi > 0 || pm === "upi" || pm === "bank/upi";
      if (activeTab === "card") return ccard > 0 || pm === "card";
      if (activeTab === "bank_transfer") return pm === "bank transfer";

      return true;
    });
  }, [orders, searchQuery, activeTab]);

  const visibleWalletTxs = useMemo(() => {
    const list = wallet?.transactions || [];
    return list.filter((tx) => {
      // Payment mode filter
      const effectivePm = getEffectivePaymentMode(tx);
      if (txFilter === "cash" && effectivePm !== "cash") return false;
      if (txFilter === "bank" && effectivePm !== "bank") return false;

      // In/Out/Transfer filter
      const meta = TX_META[tx.transaction_type] || {};
      if (txTypeFilter === "in" && meta.dir !== 1) return false;
      if (txTypeFilter === "out" && meta.dir !== -1) return false;
      if (txTypeFilter === "transfer" && meta.dir !== 0) return false;

      // Search
      if (txSearch.trim()) {
        const q = txSearch.toLowerCase();
        const p = (tx.party_name || "").toLowerCase();
        const n = (tx.notes || "").toLowerCase();
        if (!p.includes(q) && !n.includes(q)) return false;
      }
      return true;
    });
  }, [wallet?.transactions, txFilter, txTypeFilter, txSearch]);

  return (
    <div className="min-h-full bg-[#f8f9fa] p-4 md:p-6 lg:p-8 space-y-6">
      {/* ── TOP PRIMARY VIEW TOGGLER BAR ── */}
      <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4">
        {/* Dual Tab Switcher */}
        <div className="bg-stone-200/80 p-1.5 rounded-2xl flex items-center gap-1.5 w-full md:w-auto shadow-inner">
          <button
            onClick={() => setViewMode("sales")}
            className={`flex-1 md:flex-initial flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-xs font-black transition-all ${
              viewMode === "sales"
                ? "bg-white text-stone-900 shadow-sm"
                : "text-stone-600 hover:text-stone-900"
            }`}
          >
            <ShoppingBag
              size={15}
              className={viewMode === "sales" ? "text-indigo-600" : ""}
            />
            Daily Sales Transactions
            {summary.total_orders_count > 0 && (
              <span className="px-2 py-0.5 rounded-full text-[10px] bg-indigo-50 text-indigo-700 font-mono">
                {summary.total_orders_count}
              </span>
            )}
          </button>

          <button
            onClick={() => setViewMode("wallet")}
            className={`flex-1 md:flex-initial flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-xs font-black transition-all ${
              viewMode === "wallet"
                ? "bg-white text-stone-900 shadow-sm"
                : "text-stone-600 hover:text-stone-900"
            }`}
          >
            <Wallet
              size={15}
              className={viewMode === "wallet" ? "text-amber-600" : ""}
            />
            Cash &amp; Bank Drawer (Wallet)
          </button>
        </div>

        {/* Global DateRangePicker */}
        <div className="flex items-center gap-2.5 self-end md:self-auto">
          <DateRangePicker
            date={dateRange}
            onDateChange={(range) => {
              if (range) setDateRange(range);
            }}
            placeholder="Select date range"
            className="h-10 w-[240px] sm:w-[270px] rounded-xl border-stone-200 bg-white text-xs font-semibold text-stone-700 shadow-sm"
          />

          <button
            onClick={() => {
              if (viewMode === "sales") fetchSalesData(dateFrom, dateTo);
              else fetchWallet(dateFrom, dateTo);
            }}
            disabled={salesLoading || walletLoading}
            className="p-2.5 rounded-xl border border-stone-200 bg-white hover:bg-stone-50 text-stone-600 transition shadow-sm"
            title="Refresh"
          >
            <RefreshCw
              size={16}
              className={
                salesLoading || walletLoading
                  ? "animate-spin text-indigo-600"
                  : ""
              }
            />
          </button>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          VIEW 1: DAILY SALES TRANSACTIONS & DEDICATED METRIC CARDS
      ══════════════════════════════════════════════════════════════════ */}
      {viewMode === "sales" && (
        <motion.div
          key="sales-view"
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {/* Header indicator */}
          <div className="bg-white border border-stone-200 rounded-3xl p-5 shadow-sm flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-indigo-50 border border-indigo-100 rounded-2xl text-indigo-600 shadow-sm">
                <ShoppingBag size={22} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-xl font-black text-stone-900 tracking-tight">
                    Daily Sales Summary
                  </h2>
                  {isSingleDayToday && (
                    <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-200 uppercase tracking-wider">
                      Live Today
                    </span>
                  )}
                </div>
                <p className="text-xs text-stone-500 font-medium">
                  Showing sales &amp; settlement totals for{" "}
                  <span className="font-semibold text-stone-800">
                    {dateFrom === dateTo
                      ? dateFrom
                        ? format(
                            new Date(dateFrom + "T00:00:00"),
                            "EEE, dd MMM yyyy",
                          )
                        : ""
                      : `${dateFrom ? format(new Date(dateFrom + "T00:00:00"), "dd MMM yyyy") : ""} - ${
                          dateTo
                            ? format(
                                new Date(dateTo + "T00:00:00"),
                                "dd MMM yyyy",
                              )
                            : ""
                        }`}
                  </span>
                </p>
              </div>
            </div>
          </div>

          {/* Dedicated Metric Cards with Distinct Color Schemes */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3.5">
            {/* 1. TOTAL SALES (Royal Indigo) */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gradient-to-br from-indigo-900 to-indigo-950 text-white p-4 rounded-3xl shadow-sm border border-indigo-800 flex flex-col justify-between min-h-[110px]"
            >
              <div className="flex items-center justify-between opacity-80 mb-2">
                <span className="text-[10px] font-black uppercase tracking-wider text-indigo-200">
                  Total Gross Sales
                </span>
                <div className="p-1.5 bg-indigo-800/80 rounded-xl">
                  <ShoppingBag size={14} className="text-indigo-300" />
                </div>
              </div>
              <div>
                <p className="font-mono font-black text-xl tracking-tight text-white">
                  {fmtMoney(summary.total_sales)}
                </p>
                <p className="text-[10px] text-indigo-300 mt-0.5 font-medium">
                  {summary.total_orders_count || 0} invoice
                  {summary.total_orders_count === 1 ? "" : "s"}
                </p>
              </div>
            </motion.div>

            {/* 2. CASH IN VIA TODAY'S SALES (Emerald Green) */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
              className="bg-emerald-50/90 border border-emerald-200/80 p-4 rounded-3xl shadow-sm flex flex-col justify-between min-h-[110px]"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-black uppercase tracking-wider text-emerald-800">
                  Cash In via Sales
                </span>
                <div className="p-1.5 bg-emerald-100 rounded-xl text-emerald-700">
                  <Banknote size={14} />
                </div>
              </div>
              <div>
                <p className="font-mono font-black text-xl tracking-tight text-emerald-800">
                  {fmtMoney(summary.sales_cash_in !== undefined ? summary.sales_cash_in : summary.cash_in)}
                </p>
                <p className="text-[10px] text-emerald-600 mt-0.5 font-medium">
                  From today's new sales
                </p>
              </div>
            </motion.div>

            {/* 3. UPI IN VIA TODAY'S SALES (Vibrant Purple) */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-purple-50/90 border border-purple-200/80 p-4 rounded-3xl shadow-sm flex flex-col justify-between min-h-[110px]"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-black uppercase tracking-wider text-purple-800">
                  UPI In via Sales
                </span>
                <div className="p-1.5 bg-purple-100 rounded-xl text-purple-700">
                  <QrCode size={14} />
                </div>
              </div>
              <div>
                <p className="font-mono font-black text-xl tracking-tight text-purple-800">
                  {fmtMoney(summary.sales_upi_in !== undefined ? summary.sales_upi_in : summary.upi_in)}
                </p>
                <p className="text-[10px] text-purple-600 mt-0.5 font-medium">
                  QR / GPay / PhonePe
                </p>
              </div>
            </motion.div>

            {/* 4. CARD IN VIA TODAY'S SALES (Sky Blue) */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="bg-sky-50/90 border border-sky-200/80 p-4 rounded-3xl shadow-sm flex flex-col justify-between min-h-[110px]"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-black uppercase tracking-wider text-sky-800">
                  Card In via Sales
                </span>
                <div className="p-1.5 bg-sky-100 rounded-xl text-sky-700">
                  <CreditCard size={14} />
                </div>
              </div>
              <div>
                <p className="font-mono font-black text-xl tracking-tight text-sky-800">
                  {fmtMoney(summary.sales_card_in !== undefined ? summary.sales_card_in : summary.card_in)}
                </p>
                <p className="text-[10px] text-sky-600 mt-0.5 font-medium">
                  POS Machine Swipes
                </p>
              </div>
            </motion.div>

            {/* 5. BANK TRANSFER IN VIA SALES (Teal) */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-teal-50/90 border border-teal-200/80 p-4 rounded-3xl shadow-sm flex flex-col justify-between min-h-[110px]"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-black uppercase tracking-wider text-teal-800">
                  Bank Transfer In
                </span>
                <div className="p-1.5 bg-teal-100 rounded-xl text-teal-700">
                  <Landmark size={14} />
                </div>
              </div>
              <div>
                <p className="font-mono font-black text-xl tracking-tight text-teal-800">
                  {fmtMoney(summary.sales_bank_transfer_in !== undefined ? summary.sales_bank_transfer_in : summary.bank_transfer_in)}
                </p>
                <p className="text-[10px] text-teal-600 mt-0.5 font-medium">
                  NEFT / RTGS / IMPS
                </p>
              </div>
            </motion.div>

            {/* 6. BALANCE DUE (Rose / Crimson) */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
              className="bg-rose-50/90 border border-rose-200/80 p-4 rounded-3xl shadow-sm flex flex-col justify-between min-h-[110px]"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-black uppercase tracking-wider text-rose-800">
                  Balance Due
                </span>
                <div className="p-1.5 bg-rose-100 rounded-xl text-rose-700">
                  <AlertCircle size={14} />
                </div>
              </div>
              <div>
                <p className="font-mono font-black text-xl tracking-tight text-rose-700">
                  {fmtMoney(summary.balance_due)}
                </p>
                <p className="text-[10px] text-rose-500 mt-0.5 font-medium">
                  Pending credit recovery
                </p>
              </div>
            </motion.div>

            {/* 7. PENDING DELIVERY (Warm Amber) */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-amber-50/90 border border-amber-200/80 p-4 rounded-3xl shadow-sm flex flex-col justify-between min-h-[110px]"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-black uppercase tracking-wider text-amber-800">
                  Pending Delivery
                </span>
                <div className="p-1.5 bg-amber-100 rounded-xl text-amber-700">
                  <Truck size={14} />
                </div>
              </div>
              <div>
                <div className="flex items-baseline gap-1">
                  <p className="font-mono font-black text-xl tracking-tight text-amber-800">
                    {summary.pending_delivery_count || 0}
                  </p>
                  <span className="text-xs font-bold text-amber-700">
                    orders
                  </span>
                </div>
                <p className="text-[10px] text-amber-600 mt-0.5 font-medium font-mono">
                  Valued at {fmtMoney(summary.pending_delivery_value)}
                </p>
              </div>
            </motion.div>
          </div>

          {/* Settlement Banner */}
          <div className="bg-white border border-stone-200 rounded-2xl p-4 shadow-sm flex items-center gap-5 text-xs">
            <div>
              <span className="text-stone-400 uppercase tracking-wider text-[10px] font-bold block">
                Today's Sales Received
              </span>
              <span className="font-mono font-extrabold text-stone-900 text-base text-emerald-600">
                {fmtMoney(summary.total_sales_received !== undefined ? summary.total_sales_received : summary.total_received)}
              </span>
            </div>

            <div className="hidden sm:block h-7 w-px bg-stone-200" />

            <div>
              <span className="text-stone-400 uppercase tracking-wider text-[10px] font-bold block">
                Collection Rate
              </span>
              <span className="font-mono font-bold text-stone-700">
                {(
                  (Number(summary.total_sales_received !== undefined ? summary.total_sales_received : summary.total_received) /
                    (Number(summary.total_sales) || 1)) *
                  100
                ).toFixed(1)}
                % Collected
              </span>
            </div>
          </div>

          {/* Filter Pills, Search & Table */}
          <div className="bg-white border border-stone-200 rounded-3xl p-5 shadow-sm space-y-4">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
              {/* Filter Pills */}
              <div className="flex items-center gap-1.5 overflow-x-auto pb-1 sm:pb-0 scrollbar-none">
                {[
                  { key: "all", label: "All Sales", count: orders.length },
                  {
                    key: "balance_due",
                    label: "Balance Due",
                    count: orders.filter(
                      (o) => (Number(o.balance_due) || 0) > 0.01,
                    ).length,
                  },
                  {
                    key: "pending_delivery",
                    label: "Pending Delivery",
                    count: summary.pending_delivery_count,
                  },
                  { key: "cash", label: "Cash" },
                  { key: "upi", label: "UPI" },
                  { key: "card", label: "Card" },
                  { key: "bank_transfer", label: "Bank Transfer" },
                ].map((tab) => {
                  const active = activeTab === tab.key;
                  return (
                    <button
                      key={tab.key}
                      onClick={() => setActiveTab(tab.key)}
                      className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all border whitespace-nowrap flex items-center gap-1.5 ${
                        active
                          ? "bg-stone-900 text-white border-stone-900 shadow-sm"
                          : "bg-stone-50 hover:bg-stone-100 text-stone-600 border-stone-200"
                      }`}
                    >
                      {tab.label}
                      {tab.count !== undefined && (
                        <span
                          className={`px-1.5 py-0.2 rounded-full text-[10px] font-mono ${
                            active
                              ? "bg-stone-700 text-stone-200"
                              : "bg-stone-200 text-stone-700"
                          }`}
                        >
                          {tab.count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Search */}
              <div className="relative min-w-[220px]">
                <Search
                  size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none"
                />
                <input
                  type="text"
                  placeholder="Search invoice or customer..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full h-9 pl-9 pr-3 rounded-xl border border-stone-200 bg-stone-50/70 text-xs text-stone-800 placeholder-stone-400 outline-none focus:border-stone-400 focus:bg-white transition"
                />
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto rounded-2xl border border-stone-100">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-stone-50/80 border-b border-stone-200 text-[10px] font-black uppercase tracking-wider text-stone-400">
                    <th className="py-3 px-4">Time &amp; Invoice</th>
                    <th className="py-3 px-4">Customer</th>
                    <th className="py-3 px-4 text-center">Items</th>
                    <th className="py-3 px-4 text-right">Invoice Total</th>
                    <th className="py-3 px-4">Payment Breakdown</th>
                    <th className="py-3 px-4 text-right">Balance Due</th>
                    <th className="py-3 px-4 text-center">Delivery Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100 text-xs">
                  {salesLoading ? (
                    <tr>
                      <td
                        colSpan={7}
                        className="py-12 text-center text-stone-400 font-medium"
                      >
                        <RefreshCw
                          size={20}
                          className="animate-spin mx-auto mb-2 text-stone-400"
                        />
                        Loading daily sales transactions...
                      </td>
                    </tr>
                  ) : filteredOrders.length === 0 ? (
                    <tr>
                      <td
                        colSpan={7}
                        className="py-12 text-center text-stone-400 font-medium"
                      >
                        <div className="p-3 bg-stone-50 rounded-2xl w-fit mx-auto mb-2 border border-stone-100">
                          <ShoppingBag size={24} className="text-stone-300" />
                        </div>
                        No transactions found for this period.
                      </td>
                    </tr>
                  ) : (
                    filteredOrders.map((order, i) => {
                      const bal = Number(order.balance_due) || 0;
                      const total = Number(order.total) || 0;
                      const ccash = Number(order.credit_cash) || 0;
                      const cupi = Number(order.credit_upi) || 0;
                      const ccard = Number(order.credit_card) || 0;

                      let timeStr = "";
                      if (order.created_at) {
                        try {
                          timeStr = new Date(
                            order.created_at,
                          ).toLocaleTimeString("en-IN", {
                            hour: "2-digit",
                            minute: "2-digit",
                          });
                        } catch {
                          timeStr = "";
                        }
                      }

                      return (
                        <tr
                          key={order.id || order.invoice_number || i}
                          className="hover:bg-stone-50/70 transition-colors"
                        >
                          <td className="py-3 px-4">
                            <span className="font-mono font-bold text-stone-900 block">
                              {order.invoice_number}
                            </span>
                            <span className="text-[10px] text-stone-400 font-medium flex items-center gap-1 mt-0.5">
                              <Clock size={10} />
                              {timeStr || "Today"}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <span className="font-bold text-stone-800 block">
                              {order.customer_name || "Walk-in Customer"}
                            </span>
                            {order.customer_phone && (
                              <span className="text-[10px] text-stone-400 font-mono block">
                                {order.customer_phone}
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-center">
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-stone-100 text-stone-600 border border-stone-200">
                              <Package size={10} />
                              {order.items?.length || 0}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-right">
                            <span className="font-mono font-black text-stone-900 block">
                              {fmtMoney(total)}
                            </span>
                            {Number(order.extra_discount || 0) > 0 && (
                              <span className="text-[10px] text-amber-600 font-semibold block">
                                -₹{Number(order.extra_discount).toFixed(2)} disc
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              {ccash > 0 && (
                                <span className="px-2 py-0.5 rounded-lg text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                  💵 Cash: {fmtMoney(ccash)}
                                </span>
                              )}
                              {cupi > 0 && (
                                <span className="px-2 py-0.5 rounded-lg text-[10px] font-bold bg-purple-50 text-purple-700 border border-purple-200">
                                  📲 UPI: {fmtMoney(cupi)}
                                </span>
                              )}
                              {ccard > 0 && (
                                <span className="px-2 py-0.5 rounded-lg text-[10px] font-bold bg-sky-50 text-sky-700 border border-sky-200">
                                  💳 Card: {fmtMoney(ccard)}
                                </span>
                              )}
                              {ccash === 0 && cupi === 0 && ccard === 0 && (
                                <span className="px-2 py-0.5 rounded-lg text-[10px] font-bold bg-stone-100 text-stone-600 capitalize">
                                  {order.payment_mode || "Credit"}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-4 text-right">
                            {bal > 0.01 ? (
                              <span className="font-mono font-bold text-rose-600 bg-rose-50 px-2 py-0.5 rounded-lg border border-rose-200">
                                {fmtMoney(bal)}
                              </span>
                            ) : (
                              <span className="text-stone-400 text-[10px] font-semibold">
                                Settled
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-center">
                            {order.status === "delivered" ? (
                              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                <CheckCircle2 size={10} /> Delivered
                              </span>
                            ) : order.status === "pending_delivery" ? (
                              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200">
                                <Clock size={10} /> Pending Pickup
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-rose-50 text-rose-700 border border-rose-200">
                                <AlertCircle size={10} />{" "}
                                {order.status || "Partial"}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between text-xs text-stone-400 pt-2 border-t border-stone-100">
              <span>
                Displaying {filteredOrders.length} of {orders.length}{" "}
                transaction
                {orders.length === 1 ? "" : "s"}
              </span>
              <span>Showing all daily transactions</span>
            </div>
          </div>
        </motion.div>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          VIEW 2: CASH & BANK DRAWER (WALLET & HOME BALANCES)
      ══════════════════════════════════════════════════════════════════ */}
      {viewMode === "wallet" && (
        <motion.div
          key="wallet-view"
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {/* Top Live Balance Bar */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3.5">
            {[
              {
                label: "Cash Drawer Balance",
                val: wallet?.cash_balance ?? 0,
                icon: Banknote,
                bg: "bg-emerald-50/90 border-emerald-200/80",
                tc: "text-emerald-800",
                desc: "Liquid cash at counter",
              },
              {
                label: "Bank / UPI Balance",
                val: wallet?.bank_balance ?? 0,
                icon: CreditCard,
                bg: "bg-blue-50/90 border-blue-200/80",
                tc: "text-blue-800",
                desc: "Bank account & QR funds",
              },
              {
                label: "Total Shop Available",
                val: wallet?.total_balance ?? 0,
                icon: Wallet,
                bg: "bg-amber-50/90 border-amber-200/80",
                tc: "text-amber-800",
                desc: "Combined operating funds",
              },
              {
                label: "Home Funds Total",
                val: wallet?.home_total ?? 0,
                icon: Home,
                bg: "bg-purple-50/90 border-purple-200/80",
                tc: "text-purple-800",
                desc: "Cash/UPI sent home",
              },
            ].map((b) => {
              const Icon = b.icon;
              return (
                <div
                  key={b.label}
                  className={`${b.bg} border rounded-3xl p-4 shadow-sm`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Icon size={14} className={b.tc} />
                    <span className="text-[10px] font-black uppercase tracking-wider text-stone-600">
                      {b.label}
                    </span>
                  </div>
                  <p
                    className={`font-mono font-black text-2xl tracking-tight ${b.tc}`}
                  >
                    {fmtMoney(b.val)}
                  </p>
                  <p className="text-[10px] text-stone-500 mt-1">{b.desc}</p>
                </div>
              );
            })}
          </div>

          {/* Wallet Actions & History 2-column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
            {/* LEFT: Quick Record Form (Cash in/out/home) */}
            <div className="lg:col-span-5 bg-white border border-stone-200 rounded-3xl p-5 shadow-sm space-y-4">
              <div className="flex items-center justify-between pb-3 border-b border-stone-100">
                <div className="flex items-center gap-2">
                  <div className="p-2 bg-amber-50 rounded-xl text-amber-600">
                    <PlusCircle size={18} />
                  </div>
                  <div>
                    <h3 className="text-sm font-black text-stone-800">
                      Record Drawer Movement
                    </h3>
                    <p className="text-[10px] text-stone-400">
                      Cash/Bank in, out, home, or transfer
                    </p>
                  </div>
                </div>
              </div>

              {/* Action Tabs: Received | Gave | Transfer */}
              <div className="flex bg-stone-100 p-1 rounded-2xl gap-1 border border-stone-200">
                {[
                  {
                    key: "received",
                    label: "Money Received",
                    icon: ArrowDown,
                    color: "text-emerald-700 bg-white shadow-sm",
                  },
                  {
                    key: "gave",
                    label: "Money Gave / Out",
                    icon: ArrowUp,
                    color: "text-rose-700 bg-white shadow-sm",
                  },
                  {
                    key: "transfer",
                    label: "Transfer",
                    icon: ArrowLeftRight,
                    color: "text-purple-700 bg-white shadow-sm",
                  },
                ].map((t) => {
                  const Icon = t.icon;
                  const active = wTab === t.key;
                  return (
                    <button
                      key={t.key}
                      onClick={() => setWTab(t.key)}
                      className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-bold transition-all ${
                        active ? t.color : "text-stone-500 hover:text-stone-800"
                      }`}
                    >
                      <Icon size={13} />
                      {t.label}
                    </button>
                  );
                })}
              </div>

              {/* Method: Cash vs Bank/UPI */}
              {wTab !== "transfer" ? (
                <div className="grid grid-cols-2 gap-2">
                  {[
                    {
                      key: "cash",
                      label: "Cash",
                      bal: wallet?.cash_balance ?? 0,
                      icon: Banknote,
                    },
                    {
                      key: "bank",
                      label: "Bank / UPI",
                      bal: wallet?.bank_balance ?? 0,
                      icon: CreditCard,
                    },
                  ].map((m) => {
                    const Icon = m.icon;
                    const active = wVia === m.key;
                    return (
                      <button
                        key={m.key}
                        onClick={() => setWVia(m.key)}
                        className={`flex items-center gap-2.5 p-3 rounded-2xl border-2 transition-all text-left ${
                          active
                            ? "border-amber-400 bg-amber-50/70 text-amber-900"
                            : "border-stone-200 bg-stone-50 text-stone-600 hover:bg-stone-100"
                        }`}
                      >
                        <Icon size={16} />
                        <div>
                          <p className="text-xs font-bold">{m.label}</p>
                          <p className="text-[10px] font-mono text-stone-500">
                            {fmtMoney(m.bal)}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              ) : (
                /* Transfer Mode Selection */
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { key: "cash_to_bank", label: "Cash → Bank Deposit" },
                    { key: "bank_to_cash", label: "Bank → Cash Withdrawal" },
                  ].map((d) => (
                    <button
                      key={d.key}
                      onClick={() => setWDir(d.key)}
                      className={`p-2.5 rounded-2xl border-2 text-xs font-bold transition-all text-center ${
                        wDir === d.key
                          ? "border-purple-400 bg-purple-50 text-purple-900"
                          : "border-stone-200 bg-stone-50 text-stone-600 hover:bg-stone-100"
                      }`}
                    >
                      {d.label}
                    </button>
                  ))}
                </div>
              )}

              {/* Destination: Party vs Home */}
              {wTab !== "transfer" && (
                <div className="grid grid-cols-2 gap-2">
                  {[
                    {
                      key: "party",
                      label:
                        wTab === "received"
                          ? "From Customer/Party"
                          : "To Vendor/Party",
                    },
                    {
                      key: "home",
                      label:
                        wTab === "received"
                          ? "Returned from Home"
                          : "Taken Home",
                    },
                  ].map((dest) => (
                    <button
                      key={dest.key}
                      onClick={() => setWDest(dest.key)}
                      className={`h-9 rounded-xl border text-xs font-bold transition-all ${
                        wDest === dest.key
                          ? "border-amber-400 bg-amber-100/60 text-amber-900"
                          : "border-stone-200 bg-stone-50 text-stone-600 hover:bg-stone-100"
                      }`}
                    >
                      {dest.label}
                    </button>
                  ))}
                </div>
              )}

              {/* Amount Input */}
              <div className="space-y-1">
                <label className="text-[10px] font-bold uppercase text-stone-500 tracking-wider">
                  Amount (₹)
                </label>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-400 font-bold">
                    ₹
                  </span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={wAmount}
                    onChange={(e) => setWAmount(e.target.value)}
                    className="w-full h-11 pl-8 pr-4 rounded-xl border border-stone-200 text-sm font-mono font-bold text-stone-900 outline-none focus:border-amber-400 transition"
                  />
                </div>
                {wInsufficient && (
                  <p className="text-[11px] text-rose-600 font-medium flex items-center gap-1">
                    <AlertCircle size={11} /> Only {fmtMoney(wAvailBal)}{" "}
                    available in drawer
                  </p>
                )}
              </div>

              {/* Party Name Input */}
              {wTab !== "transfer" && wDest !== "home" && (
                <div className="space-y-1">
                  <label className="text-[10px] font-bold uppercase text-stone-500 tracking-wider">
                    {wTab === "received"
                      ? "Received From (Party/Customer)"
                      : "Given To (Party/Vendor)"}
                  </label>
                  <input
                    type="text"
                    placeholder="Enter person or company name..."
                    value={wParty}
                    onChange={(e) => setWParty(e.target.value)}
                    className="w-full h-10 px-3.5 rounded-xl border border-stone-200 text-xs text-stone-900 outline-none focus:border-amber-400 transition"
                  />
                </div>
              )}

              {/* Date & Notes */}
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <label className="text-[10px] font-bold uppercase text-stone-500 tracking-wider">
                    Date
                  </label>
                  <input
                    type="date"
                    value={wDate}
                    onChange={(e) => setWDate(e.target.value)}
                    className="w-full h-10 px-3 rounded-xl border border-stone-200 text-xs font-semibold text-stone-800 outline-none focus:border-amber-400 transition"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-bold uppercase text-stone-500 tracking-wider">
                    Note / Purpose
                  </label>
                  <input
                    type="text"
                    placeholder="Reason..."
                    value={wNotes}
                    onChange={(e) => setWNotes(e.target.value)}
                    className="w-full h-10 px-3 rounded-xl border border-stone-200 text-xs text-stone-800 outline-none focus:border-amber-400 transition"
                  />
                </div>
              </div>

              {/* Submit Button */}
              <button
                onClick={handleWalletSave}
                disabled={walletSaving || wInsufficient || !wAmount}
                className="w-full h-11 rounded-2xl bg-stone-900 hover:bg-stone-800 disabled:opacity-50 text-white font-bold text-xs tracking-wide shadow-md transition-all flex items-center justify-center gap-2"
              >
                {walletSaving ? (
                  <>
                    <RefreshCw size={14} className="animate-spin" />{" "}
                    Recording...
                  </>
                ) : (
                  <>
                    <Check size={14} /> Record Entry
                  </>
                )}
              </button>
            </div>

            {/* RIGHT: Cash & Bank Transaction Journal */}
            <div className="lg:col-span-7 bg-white border border-stone-200 rounded-3xl p-5 shadow-sm space-y-4">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pb-3 border-b border-stone-100">
                <div>
                  <h3 className="text-sm font-black text-stone-800">
                    Drawer Movement History
                  </h3>
                  <p className="text-[10px] text-stone-400">
                    Showing all records (no 200 item cap)
                  </p>
                </div>

                {/* Quick Filters */}
                <div className="flex items-center gap-1.5 flex-wrap">
                  {/* Mode */}
                  <div className="flex items-center bg-stone-100 p-0.5 rounded-xl border border-stone-200">
                    {["all", "cash", "bank"].map((f) => (
                      <button
                        key={f}
                        onClick={() => setTxFilter(f)}
                        className={`px-2.5 py-1 rounded-lg text-[10px] font-bold transition-all capitalize ${
                          txFilter === f
                            ? "bg-white text-stone-900 shadow-sm"
                            : "text-stone-500"
                        }`}
                      >
                        {f === "bank" ? "Bank/UPI" : f}
                      </button>
                    ))}
                  </div>

                  {/* Type */}
                  <div className="flex items-center bg-stone-100 p-0.5 rounded-xl border border-stone-200">
                    {["all", "in", "out", "transfer"].map((f) => (
                      <button
                        key={f}
                        onClick={() => setTxTypeFilter(f)}
                        className={`px-2.5 py-1 rounded-lg text-[10px] font-bold transition-all capitalize ${
                          txTypeFilter === f
                            ? "bg-white text-stone-900 shadow-sm"
                            : "text-stone-500"
                        }`}
                      >
                        {f}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Transactions List */}
              <div className="space-y-2 max-h-[560px] overflow-y-auto pr-1">
                {walletLoading ? (
                  <div className="py-12 text-center text-stone-400 text-xs">
                    <RefreshCw
                      size={20}
                      className="animate-spin mx-auto mb-2 text-stone-400"
                    />
                    Loading drawer entries...
                  </div>
                ) : visibleWalletTxs.length === 0 ? (
                  <div className="py-12 text-center text-stone-400 text-xs">
                    No transactions found for the selected period.
                  </div>
                ) : (
                  visibleWalletTxs.map((tx, i) => {
                    const meta = TX_META[tx.transaction_type] || {
                      label: tx.transaction_type,
                      dir: 0,
                    };
                    const isTransfer = meta.dir === 0;
                    const isIn = meta.dir === 1;
                    const isOut = meta.dir === -1;
                    const isHome = tx.transaction_type.includes("home");

                    return (
                      <div
                        key={tx.id || i}
                        className={`p-3.5 rounded-2xl border flex items-center justify-between gap-3 transition-colors ${
                          isHome
                            ? "bg-purple-50/60 border-purple-200"
                            : isIn
                              ? "bg-emerald-50/50 border-emerald-200"
                              : isOut
                                ? "bg-rose-50/50 border-rose-200"
                                : "bg-stone-50 border-stone-200"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className={`p-2.5 rounded-xl border ${
                              isHome
                                ? "bg-purple-100 text-purple-700 border-purple-200"
                                : isIn
                                  ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                                  : isOut
                                    ? "bg-rose-100 text-rose-700 border-rose-200"
                                    : "bg-purple-100 text-purple-700 border-purple-200"
                            }`}
                          >
                            {isHome ? (
                              <Home size={14} />
                            ) : isIn ? (
                              <ArrowDown size={14} />
                            ) : isOut ? (
                              <ArrowUp size={14} />
                            ) : (
                              <ArrowLeftRight size={14} />
                            )}
                          </div>
                          <div>
                            <div className="flex items-center gap-1.5">
                              <span className="font-bold text-xs text-stone-900">
                                {tx.party_name || meta.label}
                              </span>
                              <span
                                className={`text-[9px] font-bold px-2 py-0.5 rounded-full border ${
                                  meta.via?.includes("Cash")
                                    ? "bg-emerald-100/70 text-emerald-800 border-emerald-200"
                                    : "bg-blue-100/70 text-blue-800 border-blue-200"
                                }`}
                              >
                                {meta.via || "Direct"}
                              </span>
                            </div>
                            <p className="text-[10px] text-stone-400 mt-0.5">
                              {tx.date} {tx.notes ? `· ${tx.notes}` : ""}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <span
                              className={`font-mono font-black text-sm block ${
                                isIn
                                  ? "text-emerald-700"
                                  : isOut
                                    ? "text-rose-700"
                                    : "text-purple-700"
                              }`}
                            >
                              {isIn ? "+" : isOut ? "-" : ""}
                              {fmtMoney(tx.amount)}
                            </span>
                            <span className="text-[9px] text-stone-400 font-medium capitalize">
                              {meta.label}
                            </span>
                          </div>

                          {/* Delete for manual non-system entries */}
                          {isAdminOrManager && tx.source !== "order" && (
                            <button
                              onClick={() => handleWalletDelete(tx.id)}
                              className="p-1.5 text-stone-400 hover:text-rose-600 transition"
                              title="Delete entry"
                            >
                              <X size={13} />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
}
